import type { CrowdStrikeState, DefenderLevel, DeviceSecurityRow } from '@/types/oscar';

/** `recent` = checked in within 7 days; `over7` / `over30` = last check-in longer ago than that; `never` = no check-in recorded. */
export type CheckInFilter = '' | 'recent' | 'over7' | 'over30' | 'never';

export interface DeviceSecurityFilters {
  crowdStrike: '' | CrowdStrikeState;
  level: '' | DefenderLevel;
  checkIn: CheckInFilter;
  /** Only devices with no CrowdStrike and Defender off or not reported: the ones to look at before CrowdStrike goes. */
  needsLook: boolean;
  search: string;
}

export const noFilters: DeviceSecurityFilters = { crowdStrike: '', level: '', checkIn: '', needsLook: false, search: '' };

export const levelLabel: Record<DefenderLevel, string> = {
  full: 'Full',
  partial: 'Partial',
  off: 'Off',
  notreported: 'Not reported',
};

export const levelHint: Record<DefenderLevel, string> = {
  full: 'Malware, real-time, network inspection and tamper protection are all on',
  partial: 'Malware and real-time protection are on, but network inspection or tamper protection is not',
  off: 'Malware or real-time protection is reported off',
  notreported: 'Intune has no Defender report for this device, or left a core protection flag out',
};

/**
 * ⛔ No CrowdStrike listed AND Defender off or not reported: once CrowdStrike is gone these have nothing doing the job.
 * ⚠ "No CrowdStrike listed" is Intune's inventory as of the device's last check-in, so this is a prompt to look, not a verdict.
 */
export function needsLook(r: Pick<DeviceSecurityRow, 'CrowdStrike' | 'DefenderLevel'>): boolean {
  return r.CrowdStrike === 'absent' && (r.DefenderLevel === 'off' || r.DefenderLevel === 'notreported');
}

export interface DeviceSecuritySummary {
  total: number;
  crowdStrikePresent: number;
  crowdStrikeUnchecked: number;
  full: number;
  partial: number;
  off: number;
  notReported: number;
  needsLook: number;
}

export function summarise(rows: readonly DeviceSecurityRow[]): DeviceSecuritySummary {
  const s: DeviceSecuritySummary = { total: rows.length, crowdStrikePresent: 0, crowdStrikeUnchecked: 0, full: 0, partial: 0, off: 0, notReported: 0, needsLook: 0 };
  for (const r of rows) {
    if (r.CrowdStrike === 'present') s.crowdStrikePresent++;
    if (r.CrowdStrike === 'unchecked') s.crowdStrikeUnchecked++;
    if (r.DefenderLevel === 'full') s.full++;
    else if (r.DefenderLevel === 'partial') s.partial++;
    else if (r.DefenderLevel === 'off') s.off++;
    else s.notReported++;
    if (needsLook(r)) s.needsLook++;
  }
  return s;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * Whole days since a device last checked in to Intune, or null when none is recorded. The API sends DATETIME2 values as UTC
 * without a designator, so 'Z' is added or the browser would read them as local time.
 */
export function daysSinceCheckIn(value: string | null, now: Date): number | null {
  if (!value) return null;
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY));
}

/**
 * ⚠ A device that has not checked in lately has a stale answer in BOTH columns, so this is how to separate "Not listed
 * and recent" from "Not listed and probably just not heard from". A device with no check-in at all is its own bucket,
 * never counted as stale or as recent.
 */
export function matchesCheckIn(r: Pick<DeviceSecurityRow, 'IntuneLastCheckIn'>, f: CheckInFilter, now: Date): boolean {
  if (!f) return true;
  const days = daysSinceCheckIn(r.IntuneLastCheckIn, now);
  if (f === 'never') return days === null;
  if (days === null) return false;
  if (f === 'recent') return days <= 7;
  if (f === 'over7') return days > 7;
  return days > 30;
}

export function filterRows(rows: readonly DeviceSecurityRow[], f: DeviceSecurityFilters, now: Date = new Date()): DeviceSecurityRow[] {
  const term = f.search.trim().toLowerCase();
  return rows.filter((r) =>
    (!f.crowdStrike || r.CrowdStrike === f.crowdStrike) &&
    (!f.level || r.DefenderLevel === f.level) &&
    matchesCheckIn(r, f.checkIn, now) &&
    (!f.needsLook || needsLook(r)) &&
    (!term || [r.Hostname, r.SerialNumber, r.Model, r.AssignedTo].some((v) => (v ?? '').toLowerCase().includes(term))),
  );
}

/** A protection switch as a person says it. A switch Intune left out is "—", never "Off". */
export function switchText(value: boolean | null): string {
  if (value === null) return '—';
  return value ? 'On' : 'Off';
}

/** Reads `?crowdstrike=present&level=off&checkin=over7&look=1` so a link can land on a filtered list. Anything unrecognised is ignored. */
export function filtersFromParams(p: URLSearchParams): DeviceSecurityFilters {
  const cs = p.get('crowdstrike');
  const level = p.get('level');
  const checkIn = p.get('checkin');
  return {
    crowdStrike: cs === 'present' || cs === 'absent' || cs === 'unchecked' ? cs : '',
    level: level === 'full' || level === 'partial' || level === 'off' || level === 'notreported' ? level : '',
    checkIn: checkIn === 'recent' || checkIn === 'over7' || checkIn === 'over30' || checkIn === 'never' ? checkIn : '',
    needsLook: p.get('look') === '1',
    search: '',
  };
}
